import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { getAbandonedHotelTypeaheadEntries } from '@/components/TripActivity/utils'
import analytics from '@/shared-utils/analytics'
import { fetchAbandonedSelections } from '@/shared-utils/fetchAbandonedSelections'

import { useHotelTypeaheadLocationsFromEntries } from './useHotelTypeaheadLocationsFromEntries'

const MAX_ABANDONED_HOTEL_SEARCHES = 3

type UseAbandonedSearchesFetchedParams = {
  cguid: string
  appName: string
  appVersion: string
  signedIn: boolean
  enabled: boolean
}

export function useAbandonedSearchesFetched({
  cguid,
  appName,
  appVersion,
  signedIn,
  enabled,
}: UseAbandonedSearchesFetchedParams) {
  const { data: abandonedSelections = [], isFetched: isAbandonedFetched } = useQuery({
    queryKey: [cguid, appName, appVersion, signedIn, 'abandoned-hotel-searches'],
    queryFn: async () => {
      try {
        return await fetchAbandonedSelections({
          cguid,
          appName,
          appVersion,
          productType: ['STAY'],
          signedIn,
        })
      } catch (error) {
        analytics.logError({
          message: 'Error thrown from useQuery',
          error: JSON.stringify(error),
          service: 'fetchAbandonedSelections',
        })
        return []
      }
    },
    staleTime: Infinity,
    enabled,
  })

  const abandonedTypeaheadEntries = useMemo(
    () => getAbandonedHotelTypeaheadEntries(abandonedSelections, MAX_ABANDONED_HOTEL_SEARCHES),
    [abandonedSelections],
  )

  return useHotelTypeaheadLocationsFromEntries({
    entries: abandonedTypeaheadEntries,
    isSourceFetched: isAbandonedFetched,
  })
}
